/**
 * Legal department routes — the `/legal` parent and its ten children.
 *
 * The first modular route file in this package: `router/index.ts` is 1,547
 * lines and imports no route module, so this is registered there by one import
 * and one spread, and no screen worker ever edits that file again.
 *
 * Requirement provenance: these surface requirements come from the ideation
 * inventory. Plan: knowledge/plans/2026-09-17-legal-ops-screens.md.
 */
import type { RouteRecordRaw } from 'vue-router'

/**
 * Who may open a legal screen.
 *
 * VERBATIM MIRROR OF THE BACKEND, and the only place the pairing is written:
 *
 *   legal_matters.py:158
 *   CurrentLegal = Annotated[User, Depends(require_roles(UserRole.ADMIN, UserRole.LEGAL))]
 *
 * WHY THIS CANNOT BE `['legal']` ALONE. The shared guard
 * (shared/src/router/guards.ts) checks `!to.meta.roles.includes(userRole)` — an
 * EXACT match on the single role string, with no admin-implies-everything
 * fallback. Verified by reading the live guard, not inherited: `hasRole()` in
 * stores/auth.ts does grant admin everything, but the guard does not call it.
 * So `roles: ['legal']` would bounce all three operators — who are ADMIN — off
 * every legal screen on day one, including the ones building it.
 *
 * This is NOT the "convenient superset" S22-C1 warns against. The superset
 * failure is naming `staff`, or a broad role that happens to include you.
 * `['admin','legal']` is the real permission as the backend defines it.
 *
 * THE SWAP. The day D-1 narrows `CurrentLegal` to LEGAL alone, this constant
 * drops `'admin'` in the same commit and that is the entire frontend change.
 */
export const LEGAL_ROUTE_ROLES = ['admin', 'legal'] as const

/**
 * Declared ONCE on the parent; the ten children inherit it through vue-router's
 * per-key meta merge (child wins per key, so a child that declares no `roles`
 * takes the parent's). This is the same mechanism by which the ops AI Review
 * Queue overrides its parent's `['staff','admin']` with `['admin','developer']`.
 *
 * S22-C12's failure mode — "a route added later forgets its roles and ships
 * open" — is removed by construction: there is no per-route declaration to
 * forget. `tests/unit/legalRouteGuards.spec.ts` then asserts every resolved
 * `/legal/*` route's MERGED meta.roles deep-equals LEGAL_ROUTE_ROLES, so an
 * eleventh route that overrides the parent fails the test rather than shipping.
 */
export const legalRoutes: RouteRecordRaw[] = [
  {
    path: 'legal',
    component: () => import('@views/legal/LegalLayoutView.vue'),
    meta: {
      // Spread because meta.roles is typed as a mutable Array and the constant
      // is `as const`. The VALUES are what the guard compares, and they are
      // identical — the spec asserts that rather than trusting it.
      roles: [...LEGAL_ROUTE_ROLES],
      title: 'Legal | ClaimShield Ops',
    },
    children: [
      // Landing. `homeRouteFor()` in the shared guard resolves role `legal` to
      // THIS route name — renaming it logs every legal-only account out
      // (S22-C13), so the name is load-bearing and the spec pins it.
      {
        path: '',
        redirect: { name: 'legal-waiting' },
      },
      {
        path: 'waiting',
        name: 'legal-waiting',
        component: () => import('@views/legal/WaitingOnYouView.vue'),
        meta: { title: 'Waiting on you | Legal' },
      },
      {
        path: 'matters',
        name: 'legal-matters',
        component: () => import('@views/legal/MattersListView.vue'),
        meta: { title: 'Matters | Legal' },
      },
      {
        path: 'matters/:ref',
        name: 'legal-matter',
        component: () => import('@views/legal/MatterView.vue'),
        meta: { title: 'Matter | Legal' },
      },
      {
        path: 'matters/:ref/documents/:id',
        name: 'legal-document',
        component: () => import('@views/legal/DocumentReviewView.vue'),
        meta: { title: 'Document | Legal' },
      },
      {
        path: 'grids',
        name: 'legal-grids',
        component: () => import('@views/legal/GridsListView.vue'),
        meta: { title: 'Population grids | Legal' },
      },
      {
        path: 'grids/:id',
        name: 'legal-grid',
        component: () => import('@views/legal/GridView.vue'),
        meta: { title: 'Population grid | Legal' },
      },
      {
        path: 'jobs',
        name: 'legal-jobs',
        component: () => import('@views/legal/JobsCatalogueView.vue'),
        meta: { title: 'Jobs | Legal' },
      },
      {
        path: 'jobs/:id',
        name: 'legal-job',
        component: () => import('@views/legal/JobRunView.vue'),
        meta: { title: 'Job run | Legal' },
      },
      {
        path: 'spend',
        name: 'legal-spend',
        component: () => import('@views/legal/SpendView.vue'),
        meta: { title: 'Spend | Legal' },
      },
      // The tenth route. It appears in PRD §5.10's list and in no §5.9 screen
      // row and carries no requirement id (plan §8, finding F3) — built against
      // LegalAuditLogListResponse's own shape, with that gap recorded rather
      // than papered over.
      {
        path: 'audit',
        name: 'legal-audit',
        component: () => import('@views/legal/LegalAuditView.vue'),
        meta: { title: 'Audit log | Legal' },
      },
    ],
  },
]
